import { useState, useEffect } from 'react';
import { CalendarRange, Plus, Save, Play, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { C, fontSerif, fontMono, todayStr, addDays, fmtDateShort } from './utils';
import { incentivesAPI } from '../../lib/api';

function addMonths(dateStr, n) {
  const d = new Date(dateStr + 'T00:00:00Z');
  const day = d.getUTCDate();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() + n);
  const daysInMonth = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
  d.setUTCDate(Math.min(day, daysInMonth));
  return d.toISOString().slice(0, 10);
}

const Field = ({ label, children }) => (
  <label className="flex flex-1 flex-col gap-1">
    <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</span>
    {children}
  </label>
);

export const PeriodManagerDialog = ({ open, onOpenChange, periods, currentPeriodId, onSaved }) => {
  // new cycle
  const tomorrow = addDays(todayStr(), 1);
  const [nStart, setNStart] = useState(tomorrow);
  const [nEnd, setNEnd] = useState(addMonths(tomorrow, 1));
  const [nName, setNName] = useState('');
  const [creating, setCreating] = useState(false);

  // edit existing
  const [editId, setEditId] = useState(currentPeriodId || '');
  const [eName, setEName] = useState('');
  const [eStart, setEStart] = useState('');
  const [eEnd, setEEnd] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const autoName = `${fmtDateShort(nStart)} – ${fmtDateShort(nEnd)}`;

  useEffect(() => {
    if (open) {
      setEditId(currentPeriodId || '');
    }
  }, [open, currentPeriodId]);

  useEffect(() => {
    const p = periods.find((x) => x.id === editId);
    if (p) { setEName(p.name); setEStart(p.start_date); setEEnd(p.end_date); }
  }, [editId, periods]);

  const createCycle = async () => {
    if (!nStart || !nEnd) { toast.error('Pick start and end dates'); return; }
    if (nEnd < nStart) { toast.error('End must be after start'); return; }
    setCreating(true);
    try {
      const res = await incentivesAPI.createPeriod({
        name: nName.trim() || autoName,
        start_date: nStart,
        end_date: nEnd,
        close_others: true,
      });
      toast.success('New cycle is now active');
      onSaved?.(res.data.id);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create cycle');
    } finally {
      setCreating(false);
    }
  };

  const saveEdit = async () => {
    if (!editId) return;
    if (eEnd < eStart) { toast.error('End must be after start'); return; }
    setSavingEdit(true);
    try {
      await incentivesAPI.updatePeriod(editId, { name: eName, start_date: eStart, end_date: eEnd });
      toast.success('Cycle dates updated');
      onSaved?.(editId);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update');
    } finally {
      setSavingEdit(false);
    }
  };

  const setActive = async (id) => {
    try {
      await incentivesAPI.updatePeriod(id, { status: 'OPEN' });
      toast.success('Cycle set active');
      onSaved?.(id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not activate');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto bg-white sm:max-w-lg" data-testid="period-manager-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: fontSerif }}>
            <CalendarRange className="h-5 w-5" style={{ color: C.burgundy }} /> Manage reward cycles
          </DialogTitle>
          <DialogDescription>Run any custom window — a 15th-to-15th cycle, a sprint, or a calendar month.</DialogDescription>
        </DialogHeader>

        {/* New cycle */}
        <div className="rounded-xl border p-4" style={{ borderColor: C.border }}>
          <h4 className="mb-3 text-sm font-semibold" style={{ color: C.text }}>Start a new cycle</h4>
          <div className="flex gap-3">
            <Field label="Starts">
              <Input type="date" value={nStart} onChange={(e) => { setNStart(e.target.value); }} data-testid="new-period-start" />
            </Field>
            <Field label="Ends">
              <Input type="date" value={nEnd} onChange={(e) => setNEnd(e.target.value)} data-testid="new-period-end" />
            </Field>
          </div>
          <div className="mt-3">
            <Field label="Name">
              <Input value={nName} onChange={(e) => setNName(e.target.value)} placeholder={autoName} data-testid="new-period-name" />
            </Field>
          </div>
          <p className="mt-2 text-xs text-slate-400">Creating a new cycle closes the current one and makes this the active reward window.</p>
          <div className="mt-3 flex justify-end">
            <Button onClick={createCycle} disabled={creating} className="text-white" style={{ background: C.burgundy }} data-testid="create-period-btn">
              <Plus className="mr-1.5 h-4 w-4" /> {creating ? 'Creating…' : 'Create & activate'}
            </Button>
          </div>
        </div>

        {/* Edit existing */}
        {periods.length > 0 && (
          <div className="rounded-xl border p-4" style={{ borderColor: C.border }}>
            <h4 className="mb-3 text-sm font-semibold" style={{ color: C.text }}>Edit an existing cycle</h4>
            <div className="space-y-2">
              {periods.map((p) => (
                <div key={p.id} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm">
                  <button
                    onClick={() => setEditId(p.id)}
                    className={`text-left ${editId === p.id ? 'font-semibold' : ''}`}
                    style={{ color: editId === p.id ? C.burgundy : C.text }}
                    data-testid={`edit-period-pick-${p.id}`}
                  >
                    {p.name}
                    <span className="ml-2 text-xs text-slate-400" style={{ fontFamily: fontMono }}>{p.start_date} → {p.end_date}</span>
                  </button>
                  {p.status === 'OPEN' ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700"><CheckCircle2 className="h-3 w-3" /> active</span>
                  ) : (
                    <button onClick={() => setActive(p.id)} className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium text-slate-600 hover:bg-white" style={{ borderColor: C.border }} data-testid={`set-active-${p.id}`}>
                      <Play className="h-3 w-3" /> set active
                    </button>
                  )}
                </div>
              ))}
            </div>

            {editId && (
              <div className="mt-4 space-y-3">
                <Field label="Name">
                  <Input value={eName} onChange={(e) => setEName(e.target.value)} data-testid="edit-period-name" />
                </Field>
                <div className="flex gap-3">
                  <Field label="Starts"><Input type="date" value={eStart} onChange={(e) => setEStart(e.target.value)} data-testid="edit-period-start" /></Field>
                  <Field label="Ends"><Input type="date" value={eEnd} onChange={(e) => setEEnd(e.target.value)} data-testid="edit-period-end" /></Field>
                </div>
                <div className="flex justify-end">
                  <Button onClick={saveEdit} disabled={savingEdit} variant="outline" data-testid="save-period-btn">
                    <Save className="mr-1.5 h-4 w-4" /> {savingEdit ? 'Saving…' : 'Save dates'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
